/**
 * 登录态（token）校验 —— 全站唯一入口
 * ---------------------------------------------------------------------------
 * 背景：原先站点打开时只按「localStorage 里有没有 userdata」判断登录态，
 * 缓存里 token 早已过期也照样放行，用户能进系统、点到某个页面才逐个报错。
 * 现统一改为「以 token 为准」判断：token 有效 → 正常访问；无 token / 已过期
 * → 清登录缓存并回登录页。
 *
 * 为什么用本地解析 JWT 而不用请求后端校验：
 *   后端 HD.SystemConfig.WebApi 并未启用认证中间件（Startup 只有
 *   UseAuthorization()，没有 AddAuthentication().AddJwtBearer()），
 *   TokenActionFilter 也只是把 Authorization 头解成 Claims、不校验签名与
 *   有效期。也就是说，token 过期时接口依旧正常返回，不会给出 401。
 *   因此有效性只能由前端按 JWT 载荷中的 exp 自行判断。
 *
 * access token 由 IdentityServer（HD.AuthCenter）以 password 模式签发，
 * 默认 JWT 类型，AccessTokenLifetime = 3600 * 24 * 7（7 天），含 exp 声明。
 */

/**
 * element-ui 的 Message 与路由实例改为惰性获取：
 * 本模块在 main.js 顶部即被引入，若在此静态 import element-ui，会把
 * element-ui 的加载提前到「品牌主题 CSS」之前，破坏 main.js 顶部那段
 * 样式层叠约定；静态 import router 也会让它提前拉起整张路由表。
 * 因此只在真正需要「提示」或「跳转」时才 require。
 */
function getMessage() {
	return require("element-ui").Message;
}
function getRouter() {
	return require("../router").default;
}

const TOKEN_KEY = "logintoken";

/** 允许的时钟偏差（秒）：客户端时间略快时不至于把刚签发的 token 判成过期 */
const EXP_SKEW_SECONDS = 60;
/** 登录相关的本地缓存键；清理时只删这些，避免误伤主题(lcsj-theme)、菜单折叠等用户偏好 */
const LOGIN_CACHE_KEYS = ["userdata", "logintoken", "userId", "userInfo", "loginInfo"];
/** 过期提示防抖：多请求/多导航同时发现过期时只提醒一次 */
const NOTIFY_INTERVAL = 3000;
let lastNotifyAt = 0;

/** 读取本地 token（无则返回空串） */
export function getToken() {
	try {
		return localStorage.getItem(TOKEN_KEY) || "";
	} catch {
		return "";
	}
}

/** 读取登录用户缓存（userdata），损坏或不存在返回 null */
export function getLoginUser() {
	try {
		return JSON.parse(localStorage.getItem("userdata")) || null;
	} catch {
		return null;
	}
}

/** 登录用户缓存是否完整可用（菜单权限是路由守卫的必需字段） */
export function hasPermissionInfo(userdata) {
	const data = userdata === undefined ? getLoginUser() : userdata;
	return !!(data && Array.isArray(data.permissionInfo));
}

/** 本地是否存在登录痕迹（token 或用户缓存），用于决定是否值得清理 */
export function hasLoginCache() {
	try {
		return !!(getToken() || localStorage.getItem("userdata"));
	} catch {
		return false;
	}
}

/**
 * 解析 JWT 载荷。base64url 解码 + UTF-8 还原，失败返回 null。
 */
export function parseJwtPayload(token) {
	try {
		const parts = String(token).split(".");
		if (parts.length !== 3) return null;
		const raw = parts[1].replace(/-/g, "+").replace(/_/g, "/");
		const padded = raw.padEnd(raw.length + ((4 - (raw.length % 4)) % 4), "=");
		const binary = atob(padded);
		let json = binary;
		try {
			json = new TextDecoder("utf-8").decode(Uint8Array.from(binary, (c) => c.charCodeAt(0)));
		} catch {
			// 环境不支持 TextDecoder 时退回 atob 结果（纯 ASCII 载荷无影响）
		}
		return JSON.parse(json);
	} catch {
		return null;
	}
}

/**
 * token 是否有效：格式可解析、含 exp、且未过期（含 60s 时钟容差）。
 * 解析失败一律视为无效 —— 脏 token 发到后端会让 TokenActionFilter
 * 在 new JwtSecurityToken() 处抛异常，不如直接判为失效。
 */
export function isTokenValid(token) {
	const value = token === undefined ? getToken() : token;
	if (!value) return false;
	const payload = parseJwtPayload(value);
	if (!payload || !payload.exp) return false;
	return payload.exp * 1000 - Date.now() > EXP_SKEW_SECONDS * 1000;
}

/** 清理登录缓存（不动主题 / 菜单折叠等用户偏好） */
export function clearLoginCache() {
	try {
		LOGIN_CACHE_KEYS.forEach((key) => localStorage.removeItem(key));
	} catch {
		/* 隐私模式下 Storage 不可用，忽略 */
	}
	try {
		sessionStorage.removeItem("accessToken");
	} catch {
		/* 同上 */
	}
}

/**
 * 登录已失效的统一处理：清缓存 + 提示（3 秒内只提示一次）。
 * 跳转由调用方负责 —— 路由守卫里走 next({path:"/login"})，
 * 请求层兜底走 handleLoginExpired()。
 */
export function notifyLoginExpired(message) {
	clearLoginCache();
	const now = Date.now();
	if (now - lastNotifyAt < NOTIFY_INTERVAL) return;
	lastNotifyAt = now;
	getMessage().warning(message || "登录已过期，请重新登录！");
}

/** 请求层兜底：清缓存 + 提示 + 回登录页 */
export function handleLoginExpired(message) {
	const wasLogged = !!getToken();
	notifyLoginExpired(message);
	if (!wasLogged) return; // 本来就没登录，交给路由守卫处理，避免重复跳转
	const router = getRouter();
	const current = (router.currentRoute && router.currentRoute.path) || "";
	if (current === "/login" || current === "/") return;
	router.replace({ path: "/login" });
}
